import { Component, DestroyRef, Inject, inject, isDevMode, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AlertService } from 'src/app/services/alert.service';
import { Observable, of, map, startWith, switchMap, tap, catchError, EMPTY } from 'rxjs';
import { IUserView } from 'src/app/models/users/user-result';
import { IOrganization } from 'src/app/models/organizations/organization';
import { OrganizationsService } from 'src/app/services/organizations.service';
import { UsersService } from 'src/app/services/users.service';
import { UserCreateComponent } from '../user-create/user-create.component';
import { ValidationErrors } from 'src/app/models/validationErrors';
import sha256 from 'crypto-js/sha256';
import { environment } from 'src/environments/environment';
import { UserUpdate } from 'src/app/models/users/userUpdate';
import { CognitoKey, IUserGroups, UserType } from 'src/app/models/users/userGroups';
import { getGroupsForUserType } from 'src/app/models/users/userGroups.helpers';
import { timezoneList } from 'src/app/utils/timezone';
import { AuthService } from 'src/app/services/auth.service';
import { Permission } from 'src/app/constants/permission.constant';
import { dateTimeFormat, defaultFormat } from 'src/app/utils/dateFormat';
import { get } from 'lodash';

@Component({
    selector: 'app-user-update',
    templateUrl: './user-update.component.html',
    styleUrls: ['./user-update.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false
})
export class UserUpdateComponent {
  UserType = UserType;
  apiBase = environment.apiBase;
  Permission = Permission;
  private destroyRef = inject(DestroyRef);
  private fb = inject(FormBuilder);
  errorState: Boolean = false;
  timezoneListFiltered = timezoneList;
  dateTimeFormat = dateTimeFormat;

  nameFormControl = this.fb.nonNullable.control('', [Validators.required]);
  pinFormControl = this.fb.nonNullable.control('', [Validators.pattern('^[0-9]*$')]);
  changePinFormControl = this.fb.nonNullable.control('');
  organizationFormControl = this.fb.nonNullable.control<IOrganization | string>('', [Validators.required]);
  preferenceForm = this.fb.nonNullable.group({
    timezone: [''],
    dateFormat: [defaultFormat],
    is24H: [true],
    asAgo: [true],
  });

  constructor(
    private usersService: UsersService,
    private organizationsService: OrganizationsService,
    public authService: AuthService,
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<UserCreateComponent>,
    private alertService: AlertService,
    @Inject(MAT_DIALOG_DATA) private data: IUserView,
  ) {
    this.user = data;
    this.nameFormControl.setValue(this.user.name);
    this.pinFormControl.disable();
    this.changePinFormControl.disable();
    this.initialData();
  }
  user: IUserView;
  errorMessages: ValidationErrors[] = [];
  validatorProperties: string[] = [];
  pinLabel: string = 'Pin';
  profileImage: File | undefined;
  profileImageFile!: string | ArrayBuffer | null;
  userType: UserType = UserType.Unknown;
  initialUserType: UserType = UserType.Unknown;
  organizations: IOrganization[] = [];
  organizationFilteredOptions!: Observable<IOrganization[]>;

  initialData() {
    if (this.user.profileImageUrl == null) {
      this.profileImageFile = './assets/profile-placeholder.png';
    }

    // Determine userType from existing groups
    this.userType = this.determineUserType();
    this.initialUserType = this.userType;

    // Enable pin controls if user type requires localClientUser group
    if (this.userType === UserType.Tech || this.userType === UserType.Imagery) {
      const hasLocalClientUser = (this.user.groups || []).some(g => g.includes(CognitoKey.LocalClientUsers));
      this.changePinFormControl.enable();
      if (hasLocalClientUser) {
        this.pinLabel = 'Change Pin';
      } else {
        // User needs to set PIN since they don't have localClientUser group yet
        this.pinLabel = 'Pin';
        this.changePinFormControl.setValue('true');
        this.pinFormControl.enable();
      }
    }

    const asAgo = get(this.user, 'preference.asAgo', true);
    const is24H = get(this.user, 'preference.is24H', true);
    const initTz = get(
      this.user,
      'preference.timezone',
      Intl.DateTimeFormat().resolvedOptions().timeZone,
    );
    const tz = timezoneList.find((el) => {
      const idx = el.utc.findIndex((tz) => tz === initTz);
      return idx > -1;
    });
    this.preferenceForm.patchValue({
      timezone: tz ? tz.text : '',
      dateFormat: get(this.user, 'preference.dateTimeFormat', defaultFormat),
      is24H,
      asAgo,
    });
  }

  determineUserType(): UserType {
    const groups = (this.user.groups || []).filter(g => g?.trim());
    if (groups.length === 0) return UserType.Unknown;

    const hasSuper = groups.some((g: string) => g.includes(CognitoKey.SuperAdmins));
    const hasSetup = groups.some((g: string) => g.includes(CognitoKey.LocalSetupClientUsers));
    const hasRobot = groups.some((g: string) => g.includes(CognitoKey.LocalClientUsers));
    const hasSkyTeam = groups.some((g: string) => g.includes(CognitoKey.SkyTeamMembers));
    const hasImagery = groups.some((g: string) => g.includes(CognitoKey.ImageryTeamMembers));
    const hasOrgUsers = groups.some((g: string) => g.includes(CognitoKey.OrgUsers));

    if (hasSuper && hasSetup && hasRobot) return UserType.Tech;
    if (hasImagery) return UserType.Imagery;
    if (hasSkyTeam) return UserType.SkyTeam;
    if (hasOrgUsers) return UserType.Client;
    return UserType.Unknown;
  }

  onUserTypeChange() {
    const hasLocalClientUser = (this.user.groups || []).some(g => g.includes(CognitoKey.LocalClientUsers));
    if (this.userType === UserType.Tech || this.userType === UserType.Imagery) {
      this.pinFormControl.enable();
      this.changePinFormControl.enable();
      if (!hasLocalClientUser) {
        this.changePinFormControl.setValue('true');
        this.pinFormControl.markAllAsTouched();
      }
    } else if (this.userType === UserType.Client) {
      this.pinFormControl.disable();
      this.changePinFormControl.disable();
      this.changePinFormControl.setValue('');
      this.listOrganizations();
    } else {
      this.pinFormControl.disable();
      this.changePinFormControl.disable();
      this.changePinFormControl.setValue('');
    }
  }

  listOrganizations() {
    if (this.organizations.length === 0) {
      this.organizationsService
        .listOrganizations()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (organizations: IOrganization[]) => {
            if (organizations) {
              this.organizations = organizations;
              this.organizationFilteredOptions = this.organizationFormControl.valueChanges.pipe(
                startWith(''),
                map((value) => this.filterOrganizations(value)),
              );
            }
          },
          error: (error) => console.error('[UserUpdateComponent] error', error),
        });
    }
  }

  private filterOrganizations(value: any): IOrganization[] {
    const name = typeof value === 'string' ? value : value?.name;
    const filterValue = (name || '').toLowerCase();
    return this.organizations.filter((org) => org.name.toLowerCase().includes(filterValue));
  }

  displayFn(option: IOrganization | null): string {
    return option ? option.name : '';
  }

  onChangePin() {
    if (this.changePinFormControl.value === 'true') {
      this.pinFormControl.enable();
    } else {
      this.pinFormControl.disable();
    }
  }

  updateUserProfileImage(fileInputEvent: FileList) {
    this.profileImage = fileInputEvent[0];
    this.extractImageFromFile();
  }

  extractImageFromFile(): void {
    let fileReader: FileReader = new FileReader();
    fileReader.readAsDataURL(this.profileImage!);
    fileReader.addEventListener(
      'load',
      () => {
        this.profileImageFile = fileReader.result;
      },
      false,
    );
  }

  update() {
    this.updateProfileImage$().pipe(
      switchMap(() => this.updateGroups$()),
      switchMap(() => this.updateUser$()),
      catchError(() => EMPTY),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.dialogRef.close(true);
    });
  }

  private updateProfileImage$(): Observable<unknown> {
    if (this.profileImage == undefined) {
      return of(true);
    }
    if (isDevMode()) {
      console.log('[DEV] [UserUpdateComponent] update profile image');
    }
    return this.usersService.updateUserProfileImage(this.user.id!, this.profileImage).pipe(
      tap(() => { this.profileImage = undefined; }),
    );
  }

  private updateGroups$() {
    const requiresPin = this.userType === UserType.Tech || this.userType === UserType.Imagery;
    const hasLocalClientUser = (this.user.groups || []).some(g => g.includes(CognitoKey.LocalClientUsers));
    const needsNewPin = requiresPin && !hasLocalClientUser;

    if (needsNewPin && this.pinFormControl.value.length !== 4) {
      this.pinFormControl.markAllAsTouched();
      this.pinFormControl.setErrors({ serverErrors: 'validation errors' });
      return EMPTY;
    }

    const pinHash = needsNewPin
      ? sha256(`${this.pinFormControl.value}-${this.user.id}`).toString()
      : undefined;

    const orgValue = this.organizationFormControl.value;
    const groups = this.userType === UserType.Client
      ? [typeof orgValue !== 'string' ? orgValue?.cognitoGroupUsers : undefined].filter((g): g is string => !!g)
      : getGroupsForUserType(this.userType, environment);

    const userGroups: IUserGroups = { pinHash, groups };

    return this.usersService.updateUserGroups(this.user.id!, userGroups);
  }

  private updateUser$() {
    const requiresPin = this.userType === UserType.Tech || this.userType === UserType.Imagery;
    const pinChanged = this.pinLabel === 'Change Pin' && this.changePinFormControl.value;

    if (
      !this.preferenceForm.dirty &&
      this.user.name === this.nameFormControl.value &&
      !pinChanged
    ) {
      return of(true);
    }

    const updateUserBody: UserUpdate = {
      name: this.nameFormControl.value,
    };

    if (requiresPin && pinChanged) {
      if (this.pinFormControl.value.length === 4) {
        updateUserBody.pinHash = sha256(
          `${this.pinFormControl.value}-${this.user.id}`,
        ).toString();
      } else {
        this.pinFormControl.markAllAsTouched();
        this.pinFormControl.setErrors({ validationErrors: 'validation errors' });
        return EMPTY;
      }
    }

    const { timezone, dateFormat, asAgo, is24H } = this.preferenceForm.getRawValue();
    const tz = timezoneList.find((el) => el.text === timezone);
    updateUserBody.preference = {
      timezone: tz!.utc[0],
      dateTimeFormat: dateFormat,
      asAgo,
      is24H,
    };

    return this.usersService.updateUser(this.user.id!, updateUserBody).pipe(
      tap(() => this.alertService.successToast('Successfully updated user profile')),
    );
  }

  onKey(e: any) {
    const key = e.target.value;
    this.timezoneListFiltered = timezoneList.filter((el) => {
      const flag = el.utc.some((el) => el.toLocaleLowerCase().includes(key.toLocaleLowerCase()));
      return flag || el.text.toLocaleLowerCase().includes(key.toLocaleLowerCase());
    });
  }
}
